// src/pages/shop/NotificationsPage.js - MOBILE RESPONSIVE VERSION

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Bell,
  ShoppingCart,
  MessageCircle,
  Clock,
  X,
  Settings,
  AlertCircle,
  CheckCheck,
  CheckCircle,
  XCircle,
  DollarSign,
  MapPin,
  Star,
  AlertTriangle
} from 'lucide-react';
import NotificationService from '../../services/NotificationService';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase/config';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #0B0B3B, #1A1A4C);
  color: #FFFFFF;
  padding-bottom: 80px;

  @media (max-width: 768px) {
    padding-bottom: 100px;
  }
`;

const NotificationsHeader = styled.div`
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(128, 0, 0, 0.3);
  position: sticky;
  top: 0;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 1rem;
    position: relative;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  h1 {
    font-size: 1.8rem;
    color: #FFFFFF;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0;
    flex-shrink: 1;
    min-width: 0;

    @media (max-width: 768px) {
      font-size: 1.3rem;
      gap: 0.5rem;
    }

    @media (max-width: 380px) {
      font-size: 1.1rem;
    }

    svg {
      flex-shrink: 0;
      @media (max-width: 768px) {
        width: 20px;
        height: 20px;
      }
    }

    .title-text {
      @media (max-width: 480px) {
        display: none;
      }
    }
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;

    @media (max-width: 768px) {
      gap: 0.25rem;
    }
  }

  .unread-badge {
    background: #800000;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.8rem;
    flex-shrink: 0;

    @media (max-width: 768px) {
      padding: 0.2rem 0.4rem;
      font-size: 0.7rem;
    }
  }
`;

const HeaderButton = styled.button`
  background: ${props => props.primary ? '#800000' : 'transparent'};
  color: white;
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: all 0.3s;
  
  &:hover {
    background: ${props => props.primary ? '#600000' : 'rgba(255, 255, 255, 0.1)'};
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.85rem;
    
    svg {
      width: 16px;
      height: 16px;
    }
    
    .button-text {
      display: none;
    }
  }

  @media (max-width: 380px) {
    padding: 0.35rem 0.5rem;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const NotificationsContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const NotificationCard = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  border: 1px solid ${props => props.unread ? 'rgba(128, 0, 0, 0.6)' : 'rgba(128, 0, 0, 0.3)'};
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  
  ${props => props.unread && `
    background: rgba(128, 0, 0, 0.1);
  `}
  
  &:hover {
    transform: translateY(-2px);
    border-color: rgba(128, 0, 0, 0.8);
    background: rgba(0, 0, 0, 0.5);
  }
  
  .notification-dot {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #800000;
    display: ${props => props.unread ? 'block' : 'none'};
  }

  .icon-container {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: rgba(128, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #800000;
    flex-shrink: 0;
  }
  
  .content {
    flex: 1;
    min-width: 0;
    padding-right: 2rem;
    
    .title {
      font-weight: bold;
      margin-bottom: 0.5rem;
      word-wrap: break-word;
    }
    
    .message {
      opacity: 0.8;
      font-size: 0.9rem;
      line-height: 1.4;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    .time {
      margin-top: 0.5rem;
      font-size: 0.8rem;
      opacity: 0.6;
    }
  }
  
  .badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: bold;
    text-transform: uppercase;
    
    &.transaction {
      background: rgba(33, 150, 243, 0.2);
      color: #2196F3;
    }
    
    &.message {
      background: rgba(76, 175, 80, 0.2);
      color: #4CAF50;
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;

    .icon-container {
      width: 40px;
      height: 40px;
      
      svg {
        width: 20px;
        height: 20px;
      }
    }

    .content {
      padding-right: 1rem;
      
      .title {
        font-size: 0.95rem;
        margin-bottom: 0.4rem;
      }
      
      .message {
        font-size: 0.85rem;
      }
      
      .time {
        font-size: 0.75rem;
      }
    }

    .badge {
      position: static;
      margin-top: 0.5rem;
      display: inline-block;
      padding: 0.2rem 0.5rem;
      font-size: 0.7rem;
    }

    .notification-dot {
      top: 0.75rem;
      right: 0.75rem;
      width: 8px;
      height: 8px;
    }
  }

  @media (max-width: 480px) {
    padding: 0.85rem;
    gap: 0.75rem;
    border-radius: 8px;

    .icon-container {
      width: 36px;
      height: 36px;
      
      svg {
        width: 18px;
        height: 18px;
      }
    }

    .content {
      .title {
        font-size: 0.9rem;
      }
      
      .message {
        font-size: 0.8rem;
      }
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(255, 255, 255, 0.7);
  
  .icon {
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    margin-bottom: 1rem;
    font-size: 1.2rem;
  }

  p {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
    
    .icon svg {
      width: 40px;
      height: 40px;
    }
    
    h3 {
      font-size: 1.1rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const SettingsModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;

  .modal-content {
    background: rgba(0, 30, 60, 0.95);
    border: 1px solid rgba(128, 0, 0, 0.3);
    border-radius: 12px;
    padding: 2rem;
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;

    h3 {
      color: #800000;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.3rem;
    }

    .setting-item {
      padding: 1rem;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 8px;
      margin-bottom: 1rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;

      label {
        font-weight: 500;
        font-size: 0.95rem;
      }

      .description {
        font-size: 0.85rem;
        opacity: 0.7;
        margin-top: 0.25rem;
        line-height: 1.4;
      }

      button {
        flex-shrink: 0;
      }
    }

    .modal-buttons {
      display: flex;
      gap: 0.5rem;
      margin-top: 1.5rem;

      button {
        flex: 1;
        padding: 0.75rem;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        font-size: 0.95rem;

        &.primary {
          background: #800000;
          color: white;
        }

        &.secondary {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
        }
      }
    }

    @media (max-width: 768px) {
      padding: 1.5rem;
      max-height: 85vh;

      h3 {
        font-size: 1.2rem;
        margin-bottom: 1.25rem;
      }

      .setting-item {
        padding: 0.85rem;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;

        label {
          font-size: 0.9rem;
        }

        .description {
          font-size: 0.8rem;
        }

        button {
          width: 100%;
        }
      }

      .modal-buttons button {
        font-size: 0.9rem;
      }
    }

    @media (max-width: 480px) {
      padding: 1.25rem;

      h3 {
        font-size: 1.1rem;
      }

      .setting-item {
        padding: 0.75rem;

        label {
          font-size: 0.85rem;
        }

        .description {
          font-size: 0.75rem;
        }
      }
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  margin: 2rem auto;
  border: 3px solid rgba(128, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #800000;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    border-width: 2.5px;
  }
`;

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const navigate = useNavigate();
  const [isIOSPWA, setIsIOSPWA] = useState(false);

   // ✅ ADD: New state for countdown
  const [testCountdown, setTestCountdown] = useState(null);
  const [testType, setTestType] = useState(null);

  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';

                      const [fcmStatus, setFcmStatus] = useState({
  available: true,
  message: null
});

useEffect(() => {
  const detectIOS = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone === true ||
                        window.matchMedia('(display-mode: standalone)').matches;
    
    setIsIOSPWA(isIOS && isStandalone);
  };
  
  detectIOS();
}, []);

// ✅ ADD: Check FCM status on mount
useEffect(() => {
  const checkFCMStatus = async () => {
    if (!auth.currentUser) return;
    
    const userId = auth.currentUser.uid;
    const failed = localStorage.getItem(`fcm_failed_${userId}`);
    const reason = localStorage.getItem(`fcm_failed_reason_${userId}`);
    
    if (failed === 'true') {
      setFcmStatus({
        available: false,
        message: reason === 'timeout' 
          ? 'Push notifications unavailable (browser timeout)' 
          : 'Push notifications unavailable'
      });
    } else {
      // Try to ensure token
      const token = await NotificationService.ensureFCMToken(userId);
      
      if (!token) {
        setFcmStatus({
          available: false,
          message: 'Push notifications unavailable on this browser'
        });
      } else {
        setFcmStatus({
          available: true,
          message: null
        });
      }
    }
  };
  
  checkFCMStatus();
}, []);

  useEffect(() => {
    const initNotifications = async () => {
      if (!auth.currentUser) {
        console.log('⚠️ No user logged in');
        setLoading(false);
        return;
      }

      console.log('🔔 Setting up notifications for user:', auth.currentUser.uid);
      
      setLoading(true);
      
      try {
        const unsubscribe = NotificationService.setupNotificationsListener(
          auth.currentUser.uid,
          (newNotifications) => {
            console.log('📬 Received notifications:', newNotifications.length);
            setNotifications(newNotifications);
            setLoading(false);
          }
        );
        
        if ('Notification' in window) {
          setPushEnabled(Notification.permission === 'granted');
        }
        
        return unsubscribe;
      } catch (error) {
        console.error('❌ Error setting up notifications:', error);
        setLoading(false);
      }
    };

    const unsubscribe = initNotifications();
    
    return () => {
      if (unsubscribe && typeof unsubscribe.then === 'function') {
        unsubscribe.then(unsub => unsub && unsub());
      }
    };
  }, []);

  const handleNotificationClick = async (notification) => {
    try {
      await NotificationService.markNotificationAsRead(notification.id);
      
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

const testFullNotificationFlow = async () => {
  if (!auth.currentUser) {
    alert('Please log in first');
    return;
  }

  console.log('🧪 Scheduling FULL notification test in 60 seconds...');
  
  setTestType('full');
  setTestCountdown(60);
  
  alert('⏱️ Full Test Scheduled!\n\n' +
        'A comprehensive test will run in 60 seconds.\n\n' +
        '🧪 What gets tested:\n' +
        '  • FCM token generation\n' +
        '  • Push notification delivery\n' +
        '  • Toast display (if app open)\n' +
        '  • System notification\n' +
        '  • Badge count update\n\n' +
        'Watch your screen and notification tray!');
  
  const countdownInterval = setInterval(() => {
    setTestCountdown(prev => {
      if (prev <= 1) {
        clearInterval(countdownInterval);
        return null;
      }
      return prev - 1;
    });
  }, 1000);
  
  setTimeout(async () => {
    try {
      console.log('🚀 Executing full notification test NOW...');
      
      console.log('📝 Step 1: Ensuring FCM token...');
      const fcmToken = await NotificationService.ensureFCMToken(auth.currentUser.uid);
      
      if (!fcmToken) {
        console.warn('⚠️ No FCM token - using in-app only mode');
      } else if (fcmToken.startsWith('dev-mode-') || fcmToken.startsWith('mock-')) {
        console.log('🎭 Dev/mock token - notifications limited');
      } else {
        console.log('✅ Real FCM token found:', fcmToken.substring(0, 20) + '...');
      }
      
      console.log('📝 Step 2: Creating notification...');
      const notificationId = await NotificationService.createNotification(auth.currentUser.uid, {
        type: 'transaction',
        subType: 'test_full_flow',
        title: '🧪 Full Flow Test',
        message: 'Complete notification test! You should see both toast (if app open) and system notification.',
        actionUrl: '/notifications',
        priority: 'high'
      });
      
      console.log('✅ Notification created:', notificationId);
      
      alert('✅ Full test complete!\n\n' +
            '✓ FCM token verified\n' +
            '✓ Notification sent\n' +
            '✓ Check for toast (if app open)\n' +
            '✓ Check system notification tray');
      
      setTestType(null);
      
    } catch (error) {
      console.error('❌ Test failed:', error);
      alert('❌ Test failed: ' + error.message);
      setTestType(null);
    }
  }, 6000);
};

// ✅ REPLACE: createTestNotification function
const createTestNotification = async () => {
  if (!auth.currentUser) {
    return;
  }
  
  console.log('🧪 Scheduling simple test notification in 60 seconds...');
  
  setTestType('simple');
  setTestCountdown(60);
  
  alert('⏱️ Simple Test Scheduled!\n\n' +
        'A notification will arrive in 60 seconds.\n\n' +
        '💡 What to expect:\n' +
        '  • App open = Toast slide-up + system notification\n' +
        '  • App closed/minimized = System notification only\n\n' +
        'Try testing both scenarios!');
  
  const countdownInterval = setInterval(() => {
    setTestCountdown(prev => {
      if (prev <= 1) {
        clearInterval(countdownInterval);
        return null;
      }
      return prev - 1;
    });
  }, 1000);
  
  setTimeout(async () => {
    try {
      console.log('🚀 Executing simple test NOW...');
      
      const result = await NotificationService.createNotification(auth.currentUser.uid, {
        type: 'transaction',
        subType: 'new_order',
        title: '🛒 Test Notification',
        message: 'This is a test notification to verify the system works!',
        transactionId: 'test-' + Date.now(),
        actionUrl: '/notifications',
        priority: 'normal'
      });
      
      console.log('✅ Test notification created:', result);
      
      alert('✅ Test notification created!\n\n' +
            'If app is open: Check for toast at bottom of screen\n' +
            'Always: Check system notification tray');
      
      setTestType(null);
      
    } catch (error) {
      console.error('❌ Error creating test notification:', error);
      alert('❌ Error creating test notification: ' + error.message);
      setTestType(null);
    }
  }, 6000);
};

// ✅ ADD: Cancel test function
const cancelTest = () => {
  setTestCountdown(null);
  setTestType(null);
  AlertCircle('Test cancelled');
};

  const handleMarkAllRead = async () => {
    try {
      await NotificationService.markAllAsRead(auth.currentUser.uid);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleTogglePush = async () => {
    if (isLocalhost) {
      AlertCircle('⚠️ Push notifications are not available on localhost.\n\nIn-app notifications work fine for testing.\n\nDeploy to production to test push notifications.');
      return;
    }

    try {
      if (pushEnabled) {
        if (auth.currentUser) {
          await NotificationService.disablePushNotifications(auth.currentUser.uid);
          setPushEnabled(false);
          AlertCircle('🔕 Push notifications disabled.');
        }
      } else {
        const token = await NotificationService.requestPushPermission();
        
        if (token && auth.currentUser) {
          await NotificationService.saveFCMToken(auth.currentUser.uid, token);
          setPushEnabled(true);
          AlertCircle('✅ Push notifications enabled!');
        } else {
          AlertCircle('❌ Could not enable push notifications.');
        }
      }
    } catch (error) {
      console.error('Error toggling push:', error);
      AlertCircle('Error updating notification settings');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getIcon = (type, subType) => {
    if (type === 'message') return <MessageCircle size={24} />;
    switch (subType) {
      case 'new_order':         return <ShoppingCart size={24} />;
      case 'order_accepted':    return <CheckCircle size={24} />;
      case 'order_rejected':    return <XCircle size={24} />;
      case 'payment_received':  return <DollarSign size={24} />;
      case 'location_shared':   return <MapPin size={24} />;
      case 'completed':         return <Star size={24} />;
      case 'payment_withdrawn': return <AlertTriangle size={24} />;
      default:                  return <Bell size={24} />;
    }
  };

  const getIconColor = (type, subType) => {
    if (type === 'message') return { bg: 'rgba(76,175,80,0.18)', color: '#4CAF50' };
    switch (subType) {
      case 'new_order':         return { bg: 'rgba(33,150,243,0.18)',  color: '#2196F3' };
      case 'order_accepted':    return { bg: 'rgba(76,175,80,0.18)',   color: '#4CAF50' };
      case 'order_rejected':    return { bg: 'rgba(244,67,54,0.18)',   color: '#F44336' };
      case 'payment_received':  return { bg: 'rgba(76,175,80,0.18)',   color: '#4CAF50' };
      case 'location_shared':   return { bg: 'rgba(33,150,243,0.18)', color: '#2196F3' };
      case 'completed':         return { bg: 'rgba(255,193,7,0.18)',   color: '#FFC107' };
      case 'payment_withdrawn': return { bg: 'rgba(255,152,0,0.18)',   color: '#FF9800' };
      default:                  return { bg: 'rgba(128,0,0,0.18)',     color: '#800000' };
    }
  };

  const getBadgeLabel = (subType) => ({
    new_order:         'New Order',
    order_accepted:    'Accepted',
    order_rejected:    'Declined',
    payment_received:  'Payment',
    location_shared:   'Pickup',
    completed:         'Complete',
    payment_withdrawn: 'Withdrawn',
    new_message:       'Message',
  }[subType] || subType || 'Notification');

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <PageContainer>
      <NotificationsHeader>
        <HeaderContent>
          <h1>
            <Bell />
            <span className="title-text">Notifications</span>
            {unreadCount > 0 && (
              <span className="unread-badge">
                {unreadCount}
              </span>
            )}
          </h1>
          <div className="header-actions">
  {/* Show countdown if test is scheduled */}
  {testCountdown !== null && (
    <div style={{
      background: 'rgba(33, 150, 243, 0.2)',
      border: '1px solid rgba(33, 150, 243, 0.4)',
      borderRadius: '8px',
      padding: '0.5rem 0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      color: '#2196F3',
      fontWeight: '600'
    }}>
      <Clock size={16} />
      <span>Test in {testCountdown}s</span>
      <button
        onClick={cancelTest}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#F44336',
          cursor: 'pointer',
          padding: '0.25rem',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <X size={14} />
      </button>
    </div>
  )}
  
  <HeaderButton 
    onClick={createTestNotification} 
    disabled={testCountdown !== null}
    style={{ background: '#2196F3' }}
  >
    🧪 <span className="button-text">Test</span>
  </HeaderButton>
  
  <HeaderButton 
    onClick={testFullNotificationFlow} 
    disabled={testCountdown !== null}
    style={{ background: '#FF5722' }}
  >
    <span className="button-text">🧪 Full Test</span>
  </HeaderButton>
  
  {unreadCount > 0 && (
    <HeaderButton onClick={handleMarkAllRead}>
      <CheckCheck size={16} />
      <span className="button-text">Mark all</span>
    </HeaderButton>
  )}
  
  <HeaderButton onClick={() => setShowSettings(true)}>
    <Settings size={16} />
    <span className="button-text">Settings</span>
  </HeaderButton>
</div>
        </HeaderContent>
      </NotificationsHeader>

      {testCountdown !== null && (
  <div style={{
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem 2rem 0',
  }}>
    <div style={{
      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(0, 188, 212, 0.1))',
      border: '2px solid rgba(33, 150, 243, 0.4)',
      borderRadius: '12px',
      padding: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem'
    }}>
      <div style={{
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        background: 'rgba(33, 150, 243, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#2196F3',
        flexShrink: 0
      }}>
        {testCountdown}
      </div>
      
      <div style={{ flex: 1 }}>
        <div style={{ 
          fontWeight: 700, 
          color: '#2196F3', 
          marginBottom: '0.5rem',
          fontSize: '1.1rem'
        }}>
          ⏱️ Test Notification Scheduled
        </div>
        <div style={{ 
          fontSize: '0.95rem', 
          color: 'rgba(255, 255, 255, 0.9)',
          lineHeight: 1.5
        }}>
          {testType === 'full' ? 
            'Full notification test will trigger in ' : 
            'Simple test notification will trigger in '}
          <strong style={{ color: '#2196F3' }}>{testCountdown} seconds</strong>
        </div>
        <div style={{
          fontSize: '0.85rem',
          color: 'rgba(255, 255, 255, 0.7)',
          marginTop: '0.5rem'
        }}>
          💡 Switch to another tab or minimize the app to test different states
        </div>
      </div>
      
      <button
        onClick={cancelTest}
        style={{
          background: 'rgba(244, 67, 54, 0.2)',
          border: '1px solid rgba(244, 67, 54, 0.4)',
          borderRadius: '8px',
          padding: '0.75rem 1.25rem',
          color: '#F44336',
          cursor: 'pointer',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(244, 67, 54, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(244, 67, 54, 0.2)';
        }}
      >
        <X size={16} />
        Cancel
      </button>
    </div>
  </div>
)}

      {!fcmStatus.available && (
  <div style={{
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem 2rem 0',
  }}>
    <div style={{
      background: 'rgba(255, 193, 7, 0.1)',
      border: '1px solid rgba(255, 193, 7, 0.3)',
      borderRadius: '12px',
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    }}>
      <AlertCircle size={20} style={{ color: '#FFC107', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: '#FFC107', marginBottom: '0.25rem' }}>
          ⚠️ Limited Notifications
        </div>
        <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.9)' }}>
          {fcmStatus.message}. In-app notifications are working normally.
        </div>
      </div>
    </div>
  </div>
)}

      <NotificationsContent>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <LoadingSpinner />
            <p style={{ marginTop: '1rem', opacity: 0.7 }}>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState>
            <div className="icon">
              <Bell size={48} />
            </div>
            <h3>No notifications yet</h3>
            <p>You'll see your notifications here when you get them</p>
            <p style={{ marginTop: '1rem', opacity: 0.5, fontSize: '0.85rem' }}>
              Click the "Test" button above to create a test notification
            </p>
          </EmptyState>
        ) : (
          <NotificationsList>
            {notifications.map(notification => (
              <NotificationCard
                key={notification.id}
                unread={!notification.read}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-dot" />

                {(() => {
                  const { bg, color } = getIconColor(notification.type, notification.subType);
                  return (
                    <div className="icon-container" style={{ background: bg, color }}>
                      {getIcon(notification.type, notification.subType)}
                    </div>
                  );
                })()}

                <div className="content">
                  <div className="title">
                    {notification.title || notification.message}
                  </div>
                  {notification.title && (
                    <div className="message">{notification.message}</div>
                  )}
                  <div className="time">{formatTime(notification.timestamp)}</div>
                  <div className={`badge ${notification.type}`}>
                    {getBadgeLabel(notification.subType)}
                  </div>
                </div>
              </NotificationCard>
            ))}
          </NotificationsList>
        )}
      </NotificationsContent>

      {showSettings && (
        <SettingsModal onClick={(e) => e.target === e.currentTarget && setShowSettings(false)}>
          <div className="modal-content">
            <h3>
              <Settings size={20} />
              Notification Settings
            </h3>

            {isLocalhost && (
              <div style={{
                background: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontWeight: 600, color: '#FFC107', marginBottom: '0.5rem' }}>
                  ⚠️ Localhost Mode
                </div>
                <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.5 }}>
                  Push notifications require HTTPS and don't work on localhost.
                  <br /><br />
                  ✅ In-app notifications are working
                  <br /><br />
                  💡 To test push: Deploy to production
                </div>
              </div>
            )}

            <div className="setting-item">
  <div>
    <label>Push Notifications</label>
    <div className="description">
      {isLocalhost 
        ? 'Not available on localhost'
        : pushEnabled 
          ? 'System notifications + in-app toasts enabled'
          : 'Enable system notifications + in-app toasts'
      }
    </div>
  </div>
  <button
    onClick={handleTogglePush}
    disabled={isLocalhost}
    style={{
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      border: 'none',
      background: isLocalhost ? 'rgba(128, 128, 128, 0.3)' : 
                  pushEnabled ? '#4CAF50' : '#800000',
      color: 'white',
      cursor: isLocalhost ? 'not-allowed' : 'pointer',
      opacity: isLocalhost ? 0.5 : 1,
      minWidth: '100px'
    }}
  >
    {isLocalhost ? 'Unavailable' : 
     pushEnabled ? '✓ Enabled' : 'Enable'}
  </button>
</div>

<div className="setting-item">
  <div>
    <label>In-App Toast Notifications</label>
    <div className="description">
      Show slide-up notifications when app is open
    </div>
  </div>
  <span style={{ color: '#4CAF50', fontWeight: 600 }}>✓ Active</span>
</div>

<div className="setting-item">
  <div>
    <label>In-App Notification List</label>
    <div className="description">
      View all notifications in this page
    </div>
  </div>
  <span style={{ color: '#4CAF50', fontWeight: 600 }}>✓ Active</span>
</div>

            <div className="setting-item">
              <div>
                <label>App Badge</label>
                <div className="description">
                  Show unread count on app icon
                </div>
              </div>
              <span style={{ color: '#4CAF50' }}>✓ Active</span>
            </div>

            <div className="modal-buttons">
              <button className="secondary" onClick={() => setShowSettings(false)}>
                Close
              </button>
            </div>
          </div>
        </SettingsModal>
      )}
    </PageContainer>
  );
};

export default NotificationsPage;